import type { InviteRowStatus } from './invite-rows'

/** At most this many invite requests in flight (~3 emails/s at SES). */
export const SEND_CONCURRENCY = 5

/**
 * Worker-loop concurrency pool: at most `concurrency` runs in flight.
 * `isStopped` is checked before each dispatch; in-flight runs are never
 * aborted. `run` must not throw — per-item outcomes are the caller's concern.
 */
export async function runBatchPool<T>(
  items: readonly T[],
  concurrency: number,
  run: (item: T) => Promise<void>,
  isStopped: () => boolean,
): Promise<void> {
  let next = 0
  const worker = async () => {
    while (!isStopped() && next < items.length) {
      await run(items[next++])
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker))
}

/**
 * "Email already in use" is detected by status/code (409 / CONFLICT), never by
 * message text. Read duck-typed rather than through `instanceof ORPCError`:
 * oRPC's own type definition warns the check does not survive bundle
 * boundaries.
 */
export function classifyInviteError(error: unknown): {
  status: Extract<InviteRowStatus, 'alreadyMember' | 'failed'>
  message: string
} {
  const raw = error != null && typeof error === 'object' ? (error as Record<string, unknown>) : {}
  const isConflict = raw['status'] === 409 || raw['code'] === 'CONFLICT'
  const message =
    typeof raw['message'] === 'string' && raw['message'] ? raw['message'] : 'Request failed'
  return { status: isConflict ? 'alreadyMember' : 'failed', message }
}
